<template>
   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">

            <!-- Search -->
            <div class="col-12 col-lg-4 mb-2 mb-lg-0 d-flex gap-2">
               <input v-model="search" type="text" class="form-control form-control-sm"
                  placeholder="&#xF002; Search audit logs..."
                  style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                  @keyup="onSearch()" />
            </div>

            <!-- Filters + Pagination -->
            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort by:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Rows per page:</span>
                  <select v-model="pagination.pageSize"
                     class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                  </select>
               </div>

               <div class="pagination" v-if="pagination.totalItems > 0">
                  <button @click="previousPage()" :disabled="pagination.page == 1" class="pagination-arrow">
                     <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()"
                     :disabled="pagination.page == pagination.totalPages || pagination.totalItems == 0"
                     class="pagination-arrow">
                     <i class="fa fa-chevron-right"></i>
                  </button>
               </div>

            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col" v-for="header in headers" :key="header.value" :style="{ width: header.width }">
                        {{ header.text }}
                     </th>
                  </tr>
               </thead>

               <tbody v-if="!audit_logs || audit_logs.length === 0">
                  <tr>
                     <td :colspan="headers.length">No records to display.</td>
                  </tr>
               </tbody>

               <tbody v-else>
                  <template v-for="(log, index) in audit_logs" :key="index">
                     <tr class="clickable-row" @click="toggleRow(index)">
                        <td>
                           <span class="date-text">{{ formatDate(log.date_created) }}</span>
                        </td>
                        <td>
                           <span class="module-pill" :style="{ background: getModuleInfo(log.module).bg, color: getModuleInfo(log.module).color }">
                              <i :class="getModuleInfo(log.module).icon" class="me-1"></i>{{ log.module }}
                           </span>
                        </td>
                        <td>
                           <span class="log-type-pill" :class="getLogTypeClass(log.log_type)">
                              <i :class="getLogTypeIcon(log.log_type)" class="me-1"></i>
                              {{ formatLogType(log.log_type) }}
                           </span>
                        </td>
                        <td>
                           <span v-if="!isLogSuccess(log)" class="status-pill status-failed">
                              <i class="fa-solid fa-circle-xmark me-1"></i>Failed
                           </span>
                           <span v-else class="status-pill status-success">
                              <i class="fa-solid fa-circle-check me-1"></i>Success
                           </span>
                        </td>
                        <td>
                           <div class="d-inline-flex align-items-center gap-1 flex-wrap">
                              <span v-if="isSimulated(log.log_message)" class="simulated-pill">
                                 <i class="fa-solid fa-flask me-1"></i>Simulated
                              </span>
                              <span class="log-message" v-html="formatLogMessage(log.log_message)"></span>
                           </div>
                        </td>
                        <td>
                           <span v-if="log.performed_by && Number(log.performed_by) !== 0 && (log.performed_by_first_name || log.performed_by_last_name)" class="performer-name">
                              <i class="fa-solid fa-user me-1"></i>{{ log.performed_by_first_name }} {{ log.performed_by_last_name }}
                           </span>
                           <span v-else class="performer-system">
                              <i class="fa-solid fa-robot me-1"></i>System Generated
                           </span>
                        </td>
                        <td class="text-center" style="width: 60px;">
                           <button v-if="log.data_before || log.data_after"
                              class="btn btn-sm btn-expand"
                              :class="{ 'btn-expand-active': expandedRowIndex === index }">
                              <i :class="expandedRowIndex === index ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
                           </button>
                        </td>
                     </tr>

                     <tr v-if="expandedRowIndex === index" class="details-row">
                        <td :colspan="headers.length">
                           <div class="details-container">
                              <div v-if="log.data_before" class="data-section">
                                 <div class="data-label">
                                    <i class="fa-solid fa-clock-rotate-left me-1"></i> Data Before
                                 </div>
                                 <pre v-html="formatJson(log.data_before)"></pre>
                              </div>
                              <div v-if="log.data_after" class="data-section">
                                 <div class="data-label">
                                    <i class="fa-solid fa-arrow-right-to-bracket me-1"></i> Data After
                                 </div>
                                 <pre v-html="formatJson(log.data_after)"></pre>
                              </div>
                           </div>
                        </td>
                     </tr>
                  </template>
               </tbody>
            </table>
         </div>

      </div>
   </div>
</template>

<script>
import moment from "moment";
import { debounce } from '@/plugins/helper.js'

export default {
   data() {
      return {
         headers: [
            { text: "Date", value: "date_created", width: "170px" },
            { text: "Function", value: "module" },
            { text: "Type", value: "log_type", width: "110px" },
            { text: "Status", value: "is_success", width: "110px" },
            { text: "Action Taken", value: "log_message" },
            { text: "Executed By", value: "performed_by", width: "160px" },
            { text: "", value: "actions", width: "60px" },
         ],
         audit_logs: [],
         search: "",
         debounceFn: null,
         paginationData: {},
         expandedRowIndex: null,
         pagination: {
            pageSize: 50, page: 1, totalItems: 0, totalPages: 1,
         },
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
         sortByList: [
            { text: "Date: Newest", value: "" },
            { text: "Date: Oldest", value: "date_asc" },
            { text: "Function: A-Z", value: "module_asc" },
            { text: "Function: Z-A", value: "module_desc" },
            { text: "Type: A-Z", value: "type_asc" },
            { text: "Type: Z-A", value: "type_desc" },
         ],
         sortBy: "",
      };
   },

   computed: {
      paginationLabel() {
         var start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems)
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)
         if (this.pagination.totalItems === 0) start = 0;
         return start + " - " + end
      },
      currentPage() { return this.pagination.page },
      rowsPerPage() { return this.pagination.pageSize },
      sortItems() { return this.sortBy },
   },

   watch: {
      currentPage() { this.getAuditLogsWithFilters() },
      rowsPerPage() { this.pagination.page = 1; this.getAuditLogsWithFilters() },
      sortItems() { this.getAuditLogsWithFilters() },
   },

   created() {
      this.init()
      this.debounceFn = debounce(() => this.getAuditLogsWithFilters(), 500)
   },

   methods: {
      init() { this.getAuditLogsWithFilters() },

      async getAuditLogsWithFilters() {
         this.expandedRowIndex = null;
         let apiPath = "analytics/" + this.$store.state.auth.organisation_uuid + "/get-account-audit-logs?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page
         if (this.sortBy) { apiPath += "&order_by=" + this.sortBy }
         if (this.search) { apiPath += "&search=" + this.search }

         await this.$axios.get(apiPath)
            .then((response) => {
               if (!response.data.account_audit_logs || response.data.account_audit_logs === "No Record Found")
                  this.audit_logs = []
               else {
                  this.audit_logs = response.data.account_audit_logs
               }
               this.paginationData = response.data.pagination
               this.pagination.totalItems = this.paginationData.total_records
               this.pagination.totalPages = this.paginationData.total_pages
            })
            .catch((error) => {
               console.error("Error fetching audit logs:", error)
               this.$toast.error(error.response.data.message)
               this.audit_logs = []
               this.pagination.totalItems = 0
            })
      },

      formatDate(date) { return moment(date).format("DD-MMM-YYYY, hh:mm A") },

      formatLogType(type) {
         switch (type) {
            case 1: return "Create";
            case 2: return "Update";
            case 3: return "Delete";
            case 4: return "Auth";
            case 0: default: return "Unknown";
         }
      },

      getLogTypeClass(type) {
         switch (type) {
            case 1: return "type-create";
            case 2: return "type-update";
            case 3: return "type-delete";
            case 4: return "type-auth";
            default: return "type-unknown";
         }
      },

      getLogTypeIcon(type) {
         switch (type) {
            case 1: return "fa-solid fa-plus";
            case 2: return "fa-solid fa-pen";
            case 3: return "fa-solid fa-xmark";
            case 4: return "fa-solid fa-right-to-bracket";
            default: return "fa-solid fa-question";
         }
      },

      toggleRow(index) {
         this.expandedRowIndex = this.expandedRowIndex === index ? null : index;
      },

      formatJson(dataString) {
         if (!dataString) return '<span class="json-null">N/A</span>';
         try {
            const jsonObj = JSON.parse(dataString);
            if (jsonObj && typeof jsonObj === 'object' && !Array.isArray(jsonObj)) {
               const confidentialKeys = ['account_id', 'id', 'org_uuid', 'uuid', 'date_created'];
               confidentialKeys.forEach(key => {
                  if (jsonObj[key] !== undefined) jsonObj[key] = 'confidential information';
               });
            }
            let jsonStr = JSON.stringify(jsonObj, null, 2)
               .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

            return jsonStr.replace(/"(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?/g, (match) => {
               let cls = 'json-string';
               if (/^"confidential information"/.test(match)) cls = 'json-confidential';
               else if (/:/.test(match)) cls = 'json-key';
               return '<span class="' + cls + '">' + match + '</span>';
            })
               .replace(/\b(true|false)\b/g, '<span class="json-boolean">$1</span>')
               .replace(/\b(null)\b/g, '<span class="json-null">$1</span>')
               .replace(/(?<!")\b(-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?)\b(?!")/g, '<span class="json-number">$1</span>');
         } catch (e) {
            return dataString.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
         }
      },

      getModuleInfo(module) {
         const map = {
            'Allow List':               { icon: 'fa-solid fa-circle-check',      bg: '#e6f9ee', color: '#00873c' },
            'Block List':               { icon: 'fa-solid fa-ban',               bg: '#fde8ea', color: '#c62828' },
            'Reported Website':         { icon: 'fa-solid fa-flag',              bg: '#fff3e0', color: '#e65100' },
            'Category Settings':        { icon: 'fa-solid fa-layer-group',       bg: '#ede7f6', color: '#6a1b9a' },
            'Account Settings':         { icon: 'fa-solid fa-gear',              bg: '#e3f2fd', color: '#1565c0' },
            'General Settings':         { icon: 'fa-solid fa-gear',              bg: '#f1f3f5', color: '#455a64' },
            'API Key':                  { icon: 'fa-solid fa-key',               bg: '#fff8e1', color: '#e6a100' },
            'Extension Key':            { icon: 'fa-solid fa-puzzle-piece',      bg: '#e7f6f9', color: '#00809a' },
            'Account Domain':           { icon: 'fa-solid fa-globe',             bg: '#e3f2fd', color: '#1565c0' },
            'Account IP':               { icon: 'fa-solid fa-network-wired',     bg: '#fce4ec', color: '#c62828' },
            'Account Government TLDs':  { icon: 'fa-solid fa-landmark',          bg: '#e8eaf6', color: '#283593' },
            'Account AI Categories':    { icon: 'fa-solid fa-robot',             bg: '#f3e5f5', color: '#7b1fa2' },
            'Auth':                     { icon: 'fa-solid fa-right-to-bracket',  bg: '#fff8e1', color: '#f57f17' },
         }
         return map[module] || { icon: 'fa-solid fa-cube', bg: '#f1f3f5', color: '#6c757d' }
      },

      previousPage() { this.pagination.page > 1 ? this.pagination.page -= 1 : '' },
      nextPage() { this.pagination.totalPages > this.pagination.page ? this.pagination.page += 1 : '' },
      onSearch() { this.pagination.page = 1; this.debounceFn() },

      isSimulated(msg) {
         return !!(msg && msg.includes('[SIMULATED]'));
      },

      cleanLogMessage(msg) {
         if (!msg) return '';
         return msg.replace(/\[SIMULATED\]\s*/g, '');
      },
      formatLogMessage(msg) {
         if (!msg) return '';
         let clean = this.cleanLogMessage(msg);
         clean = clean.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
         return clean.replace(/(?:→|➔|➜|➞|-&gt;)/g, '<span class="transition-arrow"><i class="fa-solid fa-arrow-right"></i></span>');
      },
      isLogSuccess(log) {
         if (!log) return true;
         if (log.is_success === false || log.is_success === 0 || log.is_success === '0') {
            return false;
         }
         if (log.log_message && typeof log.log_message === 'string' && log.log_message.toLowerCase().startsWith('failed to')) {
            return false;
         }
         return true;
      }
   }
}
</script>

<style scoped>
.card { border: none; }

/* Date */
.date-text {
   font-size: 13px;
   color: #6c757d;
   white-space: nowrap;
}

/* Module pill */
.module-pill {
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
   gap: 2px;
   white-space: nowrap;
}

/* Log message */
.log-message {
   font-size: 13px;
   color: #435666;
   line-height: 1.5;
}

/* Transition arrow in log messages */
:deep(.transition-arrow) {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   margin: 0 6px;
   font-size: 12px;
   font-weight: 700;
   color: #00A0C1;
   vertical-align: middle;
}

/* Performer / Executed By */
.performer-name {
   font-size: 13px;
   color: #435666;
   font-weight: 500;
   white-space: nowrap;
}
.performer-name i { color: #00A0C1; }
.performer-system {
   font-size: 12px;
   color: #adb5bd;
   font-style: italic;
   white-space: nowrap;
}
.performer-system i { color: #adb5bd; }

/* Status pills */
.status-pill {
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
   gap: 3px;
   white-space: nowrap;
}
.status-success { background: #e6f9ee; color: #00873c; }
.status-failed  { background: #fde8ea; color: #c62828; }

/* Simulated badge */
.simulated-pill {
   background: #f1f5f9;
   color: #475569;
   border: 1px solid #cbd5e1;
   padding: 2px 8px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
   line-height: 1.4;
   white-space: nowrap;
}
.simulated-pill i {
   color: #64748b;
   font-size: 10px;
}

/* Log type pills */
.log-type-pill {
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
   gap: 2px;
   white-space: nowrap;
}
.type-create { background: #e6f9ee; color: #00873c; }
.type-update { background: #e7f6f9; color: #00809a; }
.type-delete { background: #fde8ea; color: #c62828; }
.type-auth   { background: #fff8e1; color: #e6a100; }
.type-unknown { background: #f1f3f5; color: #6c757d; }

/* Expand button */
.btn-expand {
   width: 28px; height: 28px;
   padding: 0;
   border: 1px solid #dee2e6;
   border-radius: 6px;
   background: #fff;
   color: #6c757d;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 11px;
   transition: all 0.15s ease;
}
.btn-expand:hover { border-color: #00A0C1; color: #00A0C1; }
.btn-expand-active { background: #00A0C1; color: #fff; border-color: #00A0C1; }

/* Clickable rows */
.clickable-row { cursor: pointer; transition: background 0.12s ease; }
.clickable-row:hover { background-color: #f9fafb; }

/* Expanded details */
.details-row td {
   background-color: #f8f9fa;
   border-top: none;
   padding: 0 !important;
}

.details-container {
   padding: 16px 24px;
   display: flex;
   gap: 20px;
}

.data-section { flex: 1; min-width: 0; }

.data-label {
   font-size: 12px;
   font-weight: 700;
   color: #435666;
   text-transform: uppercase;
   letter-spacing: 0.3px;
   margin-bottom: 8px;
}
.data-label i { color: #00A0C1; }

.data-section pre {
   background: #fff;
   border: 1px solid #e9ecef;
   padding: 12px;
   border-radius: 8px;
   max-height: 300px;
   overflow-y: auto;
   white-space: pre-wrap;
   word-break: break-all;
   font-size: 12px;
   font-family: 'Roboto Mono', monospace;
   color: #333;
   margin: 0;
}

/* JSON syntax highlighting */
.data-section :deep(.json-key) { color: #00A0C1; }
.data-section :deep(.json-string) { color: #d81b60; }
.data-section :deep(.json-number) { color: #28a745; }
.data-section :deep(.json-boolean) { color: #9c27b0; }
.data-section :deep(.json-null) { color: #999; }
.data-section :deep(.json-confidential) { color: #adb5bd; font-style: italic; }
</style>